import type { AdminConsoleUser } from '../api/adminConsole';

export type AdminUserSortKey = 'email' | 'name' | 'role' | 'groups' | 'status' | 'monitored' | 'lastLogin';
export type AdminUserSortDir = 'asc' | 'desc';

export function lastLoginTimestamp(user: AdminConsoleUser): number | null {
  if (!user.lastLoginAt) return null;
  const ms = Date.parse(user.lastLoginAt);
  return Number.isNaN(ms) ? null : ms;
}

export function formatAdminUserLastLogin(user: AdminConsoleUser): string {
  const ms = lastLoginTimestamp(user);
  if (ms == null) return 'Never';
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function groupsLabel(user: AdminConsoleUser): string {
  if (user.role === 'SUPER_ADMIN') return 'All access';
  return (user.groups ?? [])
    .map((g) => g.name)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
}

function sortValue(user: AdminConsoleUser, key: AdminUserSortKey): string {
  switch (key) {
    case 'email':
      return user.email.toLowerCase();
    case 'name':
      return user.displayName.toLowerCase();
    case 'role':
      return user.role === 'SUPER_ADMIN' ? '0-super-admin' : '1-employee';
    case 'groups':
      return groupsLabel(user).toLowerCase();
    case 'status':
      return user.isActive ? '0-active' : '1-inactive';
    case 'monitored':
      return user.isMonitored ? '0-monitored' : '1-not-monitored';
    case 'lastLogin':
      return '';
    default:
      return '';
  }
}

export function compareAdminUsers(
  a: AdminConsoleUser,
  b: AdminConsoleUser,
  key: AdminUserSortKey,
  dir: AdminUserSortDir,
): number {
  if (key === 'lastLogin') {
    const left = lastLoginTimestamp(a) ?? -1;
    const right = lastLoginTimestamp(b) ?? -1;
    const cmp = left - right;
    return dir === 'asc' ? cmp : -cmp;
  }
  const left = sortValue(a, key);
  const right = sortValue(b, key);
  const cmp = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

export function sortAdminUsers(
  items: readonly AdminConsoleUser[],
  key: AdminUserSortKey,
  dir: AdminUserSortDir,
): AdminConsoleUser[] {
  return [...items].sort((a, b) => compareAdminUsers(a, b, key, dir));
}

export function nextAdminUserSort(
  currentKey: AdminUserSortKey,
  currentDir: AdminUserSortDir,
  clicked: AdminUserSortKey,
): { key: AdminUserSortKey; dir: AdminUserSortDir } {
  if (currentKey === clicked) {
    return { key: clicked, dir: currentDir === 'asc' ? 'desc' : 'asc' };
  }
  return { key: clicked, dir: clicked === 'lastLogin' ? 'desc' : 'asc' };
}

export function canDeactivateAdminUser(
  user: AdminConsoleUser,
  allUsers: readonly AdminConsoleUser[],
): boolean {
  if (!user.isActive) return true;
  if (user.role !== 'SUPER_ADMIN') return true;
  return allUsers.filter((u) => u.role === 'SUPER_ADMIN' && u.isActive && u.id !== user.id).length > 0;
}

export function canDeleteAdminUser(
  user: AdminConsoleUser,
  allUsers: readonly AdminConsoleUser[],
  actorId: string | null | undefined,
): boolean {
  if (actorId && user.id === actorId) return false;
  if (user.role !== 'SUPER_ADMIN') return true;
  return allUsers.filter((u) => u.role === 'SUPER_ADMIN' && u.id !== user.id).length > 0;
}

export function adminUserDeleteTooltip(
  user: AdminConsoleUser,
  allUsers: readonly AdminConsoleUser[],
  actorId: string | null | undefined,
): string {
  if (actorId && user.id === actorId) return 'You cannot delete your own account';
  if (user.role === 'SUPER_ADMIN' && allUsers.filter((u) => u.role === 'SUPER_ADMIN' && u.id !== user.id).length === 0) {
    return 'Cannot delete the last super administrator';
  }
  return `Delete ${user.email}`;
}
